import { Globe, Mail, MapPin, Phone, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toWebsiteHref, toWebsiteLabel } from '../lib/website-url';
import type { Company } from '../types/companies.types';

interface CompanySummaryProps {
  company: Company;
  className?: string;
  /** When set, a dismiss button in the top-right unselects the company. */
  onClear?: () => void;
  clearLabel?: string;
}

/**
 * Read-only details of a linked company — confirms the right record is attached
 * without duplicating the fields onto whatever entity points at it.
 * Renders nothing when the company carries no details beyond its name.
 */
export function CompanySummary({
  company,
  className,
  onClear,
  clearLabel,
}: CompanySummaryProps) {
  const { website, email, phone, address } = company;
  if (!website && !email && !phone && !address) return null;

  return (
    <div
      className={cn(
        'relative space-y-1 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-globe-grey',
        // Keep the longest line clear of the dismiss button.
        onClear && 'pr-9',
        className
      )}
    >
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel}
          title={clearLabel}
          className="absolute right-1 top-1 rounded-md p-1 text-globe-grey hover:text-crimson hover:bg-background transition-colors"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {website && (
        <a
          href={toWebsiteHref(website)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 hover:text-selise-blue hover:underline"
        >
          <Globe className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{toWebsiteLabel(website)}</span>
        </a>
      )}
      {email && (
        <p className="flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{email}</span>
        </p>
      )}
      {phone && (
        <p className="flex items-center gap-1.5">
          <Phone className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{phone}</span>
        </p>
      )}
      {address && (
        <p className="flex items-start gap-1.5">
          <MapPin className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{address}</span>
        </p>
      )}
    </div>
  );
}

export default CompanySummary;
